import React from 'react';
import { Box, MenuItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SalovareSelectField from '../common/SalovareSelectField';
import SalovareDatePicker from '../common/SalovareDatePicker';
import PackageQuantityInput from '../stock/PackageQuantityInput';
import { EXPENSE_END_MODES, EXPENSE_FREQUENCIES } from '../../constants/Expenses';
import { parseCalendarDateKey, toCalendarDateKey } from '../../utils/dateFormatUtils';
import { listOccurrenceDates } from '../../utils/recurrenceUtils';
import { formatCalendarDate } from '../../utils/dateFormatUtils';

const RecurrenceFields = ({
  value,
  onChange,
  errors = {},
  startDate,
}) => {
  const { t } = useTranslation();
  const preview = listOccurrenceDates({
    startDate,
    frequency: value?.frequency,
    intervalCount: value?.intervalCount,
    endMode: value?.endMode,
    occurrenceCount: value?.occurrenceCount,
    endDate: value?.endDate,
  }).slice(0, 6);

  return (
    <Box id="recurrence-fields" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SalovareSelectField
        id="recurrence-frequency"
        label={t('expenses.recurrence.frequency')}
        required
        value={value?.frequency || 'Monthly'}
        error={Boolean(errors.frequency)}
        helperText={errors.frequency ? t(errors.frequency) : undefined}
        onChange={(event) => onChange({ ...value, frequency: event.target.value })}
      >
        {EXPENSE_FREQUENCIES.map((frequency) => (
          <MenuItem key={frequency} value={frequency}>{t(`expenses.frequency.${frequency}`)}</MenuItem>
        ))}
      </SalovareSelectField>
      <PackageQuantityInput
        id="recurrence-interval"
        label={t('expenses.recurrence.interval')}
        required
        min={1}
        step="1"
        value={value?.intervalCount ?? 1}
        error={errors.intervalCount ? t(errors.intervalCount) : undefined}
        onChange={(intervalCount) => onChange({ ...value, intervalCount })}
      />
      <SalovareSelectField
        id="recurrence-end-mode"
        label={t('expenses.recurrence.endMode')}
        required
        value={value?.endMode || 'Never'}
        onChange={(event) => onChange({ ...value, endMode: event.target.value })}
      >
        {EXPENSE_END_MODES.map((mode) => (
          <MenuItem key={mode} value={mode}>{t(`expenses.endMode.${mode}`)}</MenuItem>
        ))}
      </SalovareSelectField>
      {value?.endMode === 'AfterCount' ? (
        <PackageQuantityInput
          id="recurrence-count"
          label={t('expenses.recurrence.occurrenceCount')}
          required
          min={1}
          step="1"
          value={value?.occurrenceCount ?? ''}
          error={errors.occurrenceCount ? t(errors.occurrenceCount) : undefined}
          onChange={(occurrenceCount) => onChange({ ...value, occurrenceCount })}
        />
      ) : null}
      {value?.endMode === 'OnDate' ? (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>{t('expenses.recurrence.endDate')}</Typography>
          <SalovareDatePicker
            value={parseCalendarDateKey(value?.endDate)}
            onChange={(date) => onChange({ ...value, endDate: date ? toCalendarDateKey(date) : '' })}
            error={Boolean(errors.endDate)}
            helperText={errors.endDate ? t(errors.endDate) : undefined}
          />
        </Box>
      ) : null}
      {preview.length > 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('expenses.recurrence.preview', { dates: preview.map((date) => formatCalendarDate(date)).join(', ') })}
        </Typography>
      ) : null}
    </Box>
  );
};

export default RecurrenceFields;
